import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Mail, Link, Check } from 'lucide-react';
import { toast } from 'sonner';

const SocialShare = ({ url, title, description, compact = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || document.title;
  const shareDesc = description || '';

  const shareOptions = [
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`,
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'text-sky-500 hover:bg-sky-50 dark:hover:bg-sky-900/20',
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
      url: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareDesc}\n\n${shareUrl}`)}`,
    },
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareDesc, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    } else {
      setShowMenu(!showMenu);
    }
  };

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={handleNativeShare}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          aria-label="Share"
          data-testid="share-btn"
        >
          <Share2 className="w-4 h-4 text-gray-500" />
        </button>

        {showMenu && (
          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 p-2">
            {shareOptions.map((option) => (
              <a
                key={option.name}
                href={option.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${option.color} transition-colors`}
                onClick={() => setShowMenu(false)}
              >
                <option.icon className="w-4 h-4" />
                {option.name}
              </a>
            ))}
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 w-full transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="social-share">
      <span className="text-xs text-gray-500 font-medium">Share:</span>
      {shareOptions.map((option) => (
        <a
          key={option.name}
          href={option.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`p-2 rounded-full ${option.color} transition-colors`}
          aria-label={`Share on ${option.name}`}
        >
          <option.icon className="w-4 h-4" />
        </a>
      ))}
      <button
        onClick={handleCopyLink}
        className="p-2 rounded-full text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        aria-label="Copy link"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default SocialShare;
